import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './output.css';
import Login from './Pages/Login';
import Dashboard from './Pages/Dashboard';
import UsersPage from './Pages/Users';
import apiClient, { authAPI } from './Intercepter/APiClient';
import LogsTable from './Pages/ApiRequest';
import CLILogin from './Pages/CLILogin';
import CLIRegister from './Pages/CLIRegister';
import CheckScriptHome from './Pages/checkScriptHome';
import DeletedUsersPage from './Pages/DeletedUser';
import RemoteSupport from './Pages/RemoteSupport';
import Remotestats from './Pages/RemoteStats';
import CheckScriptPlans from './Pages/CheckScriptPlans';
import VendorActivateUser from './Pages/Vendor';
import VendorProgramLanding from './Pages/partners';
import NewRemoteSupport from './Pages/NewRemoteSupport';

// SEO Pages
import Features from './Pages/SEO/Features';
import AIAssessment from './Pages/SEO/AIAssessment';
import RemoteDesktopPage from './Pages/SEO/RemoteDesktopPage';
import NeuralDriverPage from './Pages/SEO/NeuralDriverPage';
import ScreenCapturePage from './Pages/SEO/ScreenCapturePage';
import VsCodeTantra from './Pages/SEO/VsCodeTantra';
import VsMercerMettl from './Pages/SEO/VsMercerMettl';
import VsLpuNest from './Pages/SEO/VsLpuNest';
import VsNeoBrowser from './Pages/SEO/VsNeoBrowser';
import VsDuolingo from './Pages/SEO/VsDuolingo';
import VsSafeExamBrowser from './Pages/SEO/VsSafeExamBrowser';
import VsLockDownBrowser from './Pages/SEO/VsLockDownBrowser';
import ForStudents from './Pages/SEO/ForStudents';
import ForDevelopers from './Pages/SEO/ForDevelopers';
import About from './Pages/SEO/About';
import FAQ from './Pages/SEO/FAQ';
import Contact from './Pages/SEO/Contact';
import Download from './Pages/SEO/Download';
import Pricing from './Pages/SEO/Pricing';
import BlogAITools from './Pages/SEO/Blog/BlogAITools';
import BlogAIEducation from './Pages/SEO/Blog/BlogAIEducation';
import BlogRemoteDesktop from './Pages/SEO/Blog/BlogRemoteDesktop';

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Function to verify token with the server
    const verifyToken = async () => {
      setIsVerifying(true);
      try {
        // Get token from localStorage
        const token = localStorage.getItem('auth_token');
        
        if (!token) {
          // No token found, redirect to login
          setIsAuthenticated(false);
          navigate('/', { replace: true });
          return;
        }

        // Call API to verify token
        // You'll need to create this endpoint on your server
        const response = await apiClient.get('/api/admin/verify-token');
        
        if (response.data.success) {
          setIsAuthenticated(true);
        } else {
          // Invalid token, clear localStorage and redirect
          authAPI.logout();
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Token verification error:', error);
        // Any error means we should clear localStorage and redirect
        authAPI.logout();
        navigate('/', { replace: true });
      } finally {
        setIsVerifying(false);
      }
    };

    // Only verify token if not on login page
    if (location.pathname !== '/') {
      verifyToken();
    } else {
      setIsVerifying(false);
      setIsAuthenticated(true); // No verification needed for login page
    }
  }, [navigate, location.pathname]);

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Render children only if authenticated or on login page
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Login page doesn't need protection */}
          <Route path="/" element={<CheckScriptHome />} />
          <Route path="/v1/admin/access" element={<Login />} />
          <Route path="/cli-login/:uuid" element={<CLILogin />} />
          <Route path="/cli-register" element={<CLIRegister />} />
          
          {/* SEO Routes */}
          <Route path="/features" element={<Features />} />
          <Route path="/features/ai-assessment" element={<AIAssessment />} />
          <Route path="/features/remote-desktop" element={<RemoteDesktopPage />} />
          <Route path="/features/neural-driver" element={<NeuralDriverPage />} />
          <Route path="/features/screen-capture" element={<ScreenCapturePage />} />
          
          <Route path="/alternatives/codetantra" element={<VsCodeTantra />} />
          <Route path="/alternatives/mercer-mettl" element={<VsMercerMettl />} />
          <Route path="/alternatives/lpu-nest" element={<VsLpuNest />} />
          <Route path="/alternatives/neo-browser" element={<VsNeoBrowser />} />
          <Route path="/alternatives/duolingo" element={<VsDuolingo />} />
          <Route path="/alternatives/safe-exam-browser" element={<VsSafeExamBrowser />} />
          <Route path="/alternatives/lockdown-browser" element={<VsLockDownBrowser />} />
          
          <Route path="/use-cases/students" element={<ForStudents />} />
          <Route path="/use-cases/developers" element={<ForDevelopers />} />
          
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          
          <Route path="/download" element={<Download />} />
          <Route path="/pricing" element={<Pricing />} />
          
          <Route path="/blog/best-ai-study-tools-2026" element={<BlogAITools />} />
          <Route path="/blog/how-ai-changing-education" element={<BlogAIEducation />} />
          <Route path="/blog/remote-desktop-guide" element={<BlogRemoteDesktop />} />
          
          {/* Protected routes */}
          <Route 
            path="/console" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/api-requests" 
            element={
              <ProtectedRoute>
                <LogsTable />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users/deleted" 
            element={
              <ProtectedRoute>
                <DeletedUsersPage />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/remote" 
            element={
              
                <RemoteSupport />
              
            } 
          />
          <Route 
            path="/secure/remote" 
            element={
              
                <RemoteSupport />
              
            } 
          />
          <Route 
            path="/remote/new" 
            element={
              
                <NewRemoteSupport />
              
            } 
          />
          <Route 
            path="/vendor/activate" 
            element={
              
                <VendorActivateUser />
              
            } 
          />
          <Route 
            path="/partners" 
            element={
              
                <VendorProgramLanding />
              
            } 
          />
          <Route 
            path="/plans" 
            element={
              
                <CheckScriptPlans />
              
            } 
          />
          <Route 
            path="/remote/stats" 
            element={
              <ProtectedRoute>
                <Remotestats />
              </ProtectedRoute>
            } 
          />
          
          {/* Redirect all other routes to login */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;